import React, { useEffect, useState } from 'react';
import { Modal, View, Text, StyleSheet, ScrollView, TouchableOpacity, TextInput } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import * as Clipboard from 'expo-clipboard';

import { useTheme } from '../context/ThemeContext';
import type { ColorTheme } from '../utils/theme';
import { spacing, fontSize, fontWeight, borderRadius } from '../utils/theme';
import Button from './Button';
import { useToast } from './Toast';
import { api } from '../api/client';
import { config } from '../config';
import { openExternal } from '../utils/openExternal';

interface Props {
  visible: boolean;
  accountNumber: string;
  /** 'pre-stripe' shows a Continue-to-Stripe button; 'manage' shows Save & Close. */
  mode: 'pre-stripe' | 'manage';
  onClose: () => void;
  /** Only called in pre-stripe mode after policies save successfully. */
  onContinue?: () => void;
}

interface PoliciesResponse {
  refundPolicy: string | null;
  returnPolicy: string | null;
  cancellationPolicy: string | null;
  defaults: { refund: string; return: string; cancellation: string };
}


export default function StripePrepModal({ visible, accountNumber, mode, onClose, onContinue }: Props) {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const toast = useToast();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [refund, setRefund] = useState('');
  const [returnP, setReturnP] = useState('');
  const [cancel, setCancel] = useState('');

  const publicUrl = `${config.appUrl}/vendor/${accountNumber}`;

  useEffect(() => {
    if (!visible) return;
    setLoading(true);
    api<PoliciesResponse>('/api/owner/policies')
      .then(d => {
        setRefund(d.refundPolicy ?? d.defaults.refund);
        setReturnP(d.returnPolicy ?? d.defaults.return);
        setCancel(d.cancellationPolicy ?? d.defaults.cancellation);
      })
      .catch(err => toast.show({ type: 'error', title: 'Could not load policies', message: err?.message }))
      .finally(() => setLoading(false));
  }, [visible]);

  const copyUrl = async () => {
    try {
      await Clipboard.setStringAsync(publicUrl);
      toast.show({ type: 'success', title: 'Link copied' });
    } catch {
      toast.show({ type: 'error', title: 'Copy failed', message: 'Please copy manually.' });
    }
  };

  const openPreview = () => {
    openExternal(publicUrl);
  };

  const savePolicies = async (): Promise<boolean> => {
    setSaving(true);
    try {
      await api('/api/owner/policies', {
        method: 'PATCH',
        body: {
          refundPolicy: refund,
          returnPolicy: returnP,
          cancellationPolicy: cancel,
        },
      });
      toast.show({ type: 'success', title: 'Policies saved' });
      setSaving(false);
      return true;
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Save failed', message: err?.message });
      setSaving(false);
      return false;
    }
  };

  const handlePrimary = async () => {
    const ok = await savePolicies();
    if (!ok) return;
    if (mode === 'pre-stripe') {
      onContinue?.();
    } else {
      onClose();
    }
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={styles.backdrop}>
        <View style={styles.card}>
          <View style={styles.header}>
            <Text style={styles.title}>
              {mode === 'pre-stripe' ? 'Before connecting Stripe' : 'KYC Page and Policies'}
            </Text>
            <TouchableOpacity onPress={onClose} hitSlop={12}>
              <Ionicons name="close" size={24} color={colors.text} />
            </TouchableOpacity>
          </View>

          <ScrollView contentContainerStyle={styles.body}>
            <Text style={styles.intro}>
              {mode === 'pre-stripe'
                ? 'Stripe requires a public "Business website" URL during onboarding. We\'ve generated one for your business — it automatically stays in sync with your locations, bays, and policies.'
                : 'This is the public page Stripe uses for KYC. It stays in sync with your locations, bays, and policies automatically.'}
            </Text>

            <Text style={styles.label}>Your public vendor URL</Text>
            <View style={styles.urlRow}>
              <Text style={styles.url} selectable numberOfLines={1}>{publicUrl}</Text>
              <TouchableOpacity onPress={copyUrl} style={styles.copyBtn}>
                <Ionicons name="copy-outline" size={18} color={colors.primary} />
                <Text style={styles.copyText}>Copy</Text>
              </TouchableOpacity>
            </View>
            <TouchableOpacity onPress={openPreview} style={{ marginTop: spacing.xs }}>
              <Text style={styles.previewLink}>Preview page <Ionicons name="open-outline" size={12} /></Text>
            </TouchableOpacity>

            {mode === 'pre-stripe' && (
              <View style={styles.instructionBox}>
                <Ionicons name="information-circle-outline" size={18} color={colors.primary} />
                <View style={{ flex: 1 }}>
                  <Text style={styles.instructionText}>
                    1. When Stripe asks for your <Text style={{ fontWeight: fontWeight.semibold }}>Business website</Text>,
                    paste this URL into that field.
                  </Text>
                  <Text style={[styles.instructionText, { marginTop: spacing.xs }]}>
                    2. Immediately after finishing Stripe setup, add at least{' '}
                    <Text style={{ fontWeight: fontWeight.semibold }}>one location and one bay</Text>
                    {' '}so your page shows real services and you can start accepting payments.
                  </Text>
                </View>
              </View>
            )}

            <Text style={styles.sectionTitle}>Policies shown on the page</Text>
            <Text style={styles.helper}>Edit the generic defaults below to match your business. These save when you continue.</Text>

            <Text style={styles.label}>Refund Policy</Text>
            <TextInput
              style={styles.textarea}
              value={refund}
              onChangeText={setRefund}
              multiline
              editable={!loading}
              placeholderTextColor={colors.textLight}
            />

            <Text style={styles.label}>Return Policy</Text>
            <TextInput
              style={styles.textarea}
              value={returnP}
              onChangeText={setReturnP}
              multiline
              editable={!loading}
              placeholderTextColor={colors.textLight}
            />

            <Text style={styles.label}>Cancellation Policy</Text>
            <TextInput
              style={styles.textarea}
              value={cancel}
              onChangeText={setCancel}
              multiline
              editable={!loading}
              placeholderTextColor={colors.textLight}
            />
          </ScrollView>

          <View style={styles.footer}>
            <Button title="Cancel" variant="outline" onPress={onClose} style={{ flex: 1 }} />
            <Button
              title={saving ? 'Saving…' : mode === 'pre-stripe' ? 'Continue to Stripe' : 'Save & Close'}
              onPress={handlePrimary}
              disabled={saving || loading}
              style={{ flex: 1 }}
            />
          </View>
        </View>
      </View>
    </Modal>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  backdrop: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'center', alignItems: 'center', padding: spacing.md },
  card: { width: '100%', maxWidth: 560, maxHeight: '90%', backgroundColor: c.background, borderRadius: borderRadius.lg, overflow: 'hidden' },
  header: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: spacing.md, borderBottomWidth: 1, borderBottomColor: c.borderLight },
  title: { fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: c.text },
  body: { padding: spacing.md },
  intro: { fontSize: fontSize.sm, color: c.textSecondary, lineHeight: 20, marginBottom: spacing.md },
  label: { fontSize: fontSize.xs, fontWeight: fontWeight.semibold, color: c.text, marginTop: spacing.sm, marginBottom: spacing.xs },
  urlRow: { flexDirection: 'row', alignItems: 'center', backgroundColor: c.surface, borderWidth: 1, borderColor: c.borderLight, borderRadius: borderRadius.sm, paddingHorizontal: spacing.sm, paddingVertical: spacing.xs },
  url: { flex: 1, fontSize: fontSize.sm, color: c.text, marginRight: spacing.sm },
  copyBtn: { flexDirection: 'row', alignItems: 'center', gap: 4, padding: spacing.xs },
  copyText: { fontSize: fontSize.xs, color: c.primary, fontWeight: fontWeight.semibold },
  previewLink: { fontSize: fontSize.xs, color: c.primary },
  instructionBox: { flexDirection: 'row', gap: spacing.sm, alignItems: 'flex-start', backgroundColor: c.surface, borderRadius: borderRadius.sm, padding: spacing.sm, marginTop: spacing.md, borderWidth: 1, borderColor: c.borderLight },
  instructionText: { flex: 1, fontSize: fontSize.sm, color: c.text, lineHeight: 20 },
  sectionTitle: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text, marginTop: spacing.lg, marginBottom: spacing.xs },
  helper: { fontSize: fontSize.xs, color: c.textSecondary, marginBottom: spacing.sm },
  textarea: { backgroundColor: c.surface, borderWidth: 1, borderColor: c.borderLight, borderRadius: borderRadius.sm, padding: spacing.sm, color: c.text, fontSize: fontSize.sm, minHeight: 116, textAlignVertical: 'top' },
  footer: { flexDirection: 'row', gap: spacing.sm, padding: spacing.md, borderTopWidth: 1, borderTopColor: c.borderLight },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
